<template>
  <div class="AiRoomCard">
    <div class="AiRoomCard-body">
      <div v-if="image" class="AiRoomCard-image">
        <ai-image
          :src="image.src"
          :alt="image.alt"
          :src-set="image?.srcSet as string"
          loading="lazy" />
      </div>
      <div v-else class="AiRoomCard-image AiRoomCard-imagePlaceholder">
        <ai-icon
          name="photo"
          :size="32"
          class="AiRoomCard-imagePlaceholderIcon" />
      </div>

      <ai-button
        slim
        small
        variant="tertiary"
        @click="detailsDrawerOpen = true">
        <ai-typo class="AiRoomCard-title" as="span" variant="heading-04">
          {{ title }}
        </ai-typo>
      </ai-button>

      <div class="AiRoomCard-facilities">
        <div
          v-for="amenity of amenities"
          :key="amenity.key"
          class="AiRoomCard-facilitiesItem">
          <ai-icon
            :namespace="amenity.iconNamespace ?? 'global'"
            :name="amenity.icon ?? 'help'"
            class="AiRoomCard-facilitiesItemIcon" />

          <ai-typo variant="paragraph-04-regular" as="span">
            {{ amenity.label }}
          </ai-typo>
        </div>
      </div>
    </div>

    <div class="AiRoomCard-actions">
      <ai-button
        ref="seeFullDetailsButton"
        variant="tertiary"
        :label="$t('ux.molecules.roomCard.fullDetailsCTA')"
        :icon-right="'button-arrow-right' as IconName"
        slim
        small
        :aria-label="
          $t('ux.molecules.roomCard.fullDetailsCTA', { label: title })
        "
        @click="detailsDrawerOpen = true" />

      <div
        v-if="typeof isAvailable === 'boolean'"
        class="AiRoomCard-availabilityButton">
        <ai-typo
          as="p"
          variant="paragraph-03-regular"
          class="AiRoomCard-availabilityButton-availabilityText">
          {{
            !isAvailable
              ? $t('ux.molecules.roomCard.notAvailableText')
              : '&nbsp;'
          }}
        </ai-typo>
        <ai-button
          :variant="isAvailable ? 'primary' : 'secondary'"
          :label="
            isAvailable
              ? $t('ux.molecules.roomCard.available')
              : $t('ux.molecules.roomCard.notAvailable')
          "
          :url="availabilityUrl"
          as-link />
      </div>
    </div>

    <ai-room-details
      v-model="detailsDrawerOpen"
      :room="room"
      @popin-close="closeDetails" />
  </div>
</template>

<script setup lang="ts">
import type { IconName, IconNameSpace } from '../../atoms/AiIcon/types';
import AiRoomDetails from '../../organisms/AiRoomDetails/AiRoomDetails.vue';

import type { UnconfiguredGuests } from '~~/domains/booking';
import {
  type Accommodation,
  useGlobalConfigurationAsyncData,
} from '~~/domains/graphql';
import { useUserStore } from '~~/domains/user';

type Amenity = {
  iconNamespace: IconNameSpace;
  icon?: IconName;
  label: string;
  key: string;
};

interface AiRoomCardProps {
  title: string;
  image: AiImageProps;
  room?: Accommodation;
  amenities: Amenity[];
  slideIndex: number;
  useRouteQuery?: boolean;
  isAvailable?: boolean;
  bookingUrl?: string;
  hotelReferenceId?: string;
  dates?: Date[];
  roomsAndGuests?: UnconfiguredGuests[];
}

const props = defineProps<AiRoomCardProps>();
const userStore = useUserStore();
const { data: globalConfigurationData } = useGlobalConfigurationAsyncData();
const { getRedirectToAvailabilityUrl } = useExternalNavigation();

const availabilityUrl = computed(() => {
  const bookingConfig =
    globalConfigurationData.value?.globalConfiguration?.booking;

  if (
    !bookingConfig?.useExternalRoomsPage ||
    !bookingConfig.externalSearchPageUrl ||
    !props.hotelReferenceId ||
    !props.dates?.[0] ||
    !props.dates?.[1]
  ) {
    return props.bookingUrl;
  }

  return getRedirectToAvailabilityUrl(bookingConfig.externalSearchPageUrl, {
    hotelReferenceId: props.hotelReferenceId,
    dates: props.dates,
    roomsAndGuests: props.roomsAndGuests?.length
      ? props.roomsAndGuests
      : [{ adults: 2, childrenAges: [] }],
    isLogged: userStore.isLogged,
    roomCode: props.isAvailable ? (props.room?.id ?? undefined) : undefined,
  });
});

const detailsOpen = ref(false);
const route = useRoute();
const router = useRouter();
const seeFullDetailsButton = ref();

const routeParam = computed(() => `${props.room?.id}-${props.slideIndex}`);

const detailsDrawerOpen = computed({
  get() {
    if (props.useRouteQuery) {
      const roomDetails = route.query?.roomDetails as string | undefined;

      return (
        Boolean(roomDetails) && (roomDetails as string) === routeParam.value
      );
    }

    return detailsOpen.value;
  },
  set(value: boolean) {
    if (props.useRouteQuery) {
      const roomDetails = value ? routeParam.value : undefined;

      router.push({ query: { roomDetails } });
      return;
    }

    detailsOpen.value = value;
  },
});

const closeDetails = () => {
  detailsDrawerOpen.value = !detailsDrawerOpen.value;
  seeFullDetailsButton.value.$el.focus();
};
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/functions' as func;

.AiRoomCard {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 100%;
}

.AiRoomCard-image {
  height: func.calcRem(200);

  @media (mq.$from-medium) {
    height: func.calcRem(280);
  }
}

.AiRoomCard-imagePlaceholder {
  background: colors.$neutral-300;
  display: flex;
  align-items: center;
  justify-content: center;
}

.AiRoomCard-imagePlaceholderIcon {
  color: colors.$neutral-500;
}

.AiRoomCard-image img {
  width: 100%;
}

.AiRoomCard-title {
  margin-top: constants.$margin-02;
}

.AiRoomCard-facilities {
  margin-top: constants.$margin-01;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0;

  @media (mq.$from-medium) {
    column-gap: constants.$margin-02;
  }
}

.AiRoomCard-facilitiesItem {
  display: flex;
  width: 100%;
  margin-bottom: constants.$margin-00;

  &:last-child {
    margin-bottom: 0;
  }
  @media (mq.$from-medium) {
    width: calc(50% - constants.$margin-02);
    &:last-child {
      margin-bottom: constants.$margin-00;
    }
  }
}

.AiRoomCard-facilitiesItemIcon {
  color: colors.$stratos-600;
  margin-right: constants.$margin-00;
  margin-top: 0.2rem;
}

.AiRoomCard-actions {
  margin-top: constants.$margin-00;
}

.AiRoomCard-availabilityButton {
  margin-top: constants.$margin-00;

  &-availabilityText {
    margin-bottom: constants.$margin-00;
  }
}
</style>
